import { Injectable, OnDestroy, signal } from '@angular/core';
import { from, fromEvent, Observable, Subscription, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/** Interval for background connectivity checks (1 minute) */
const BACKGROUND_CONNECTIVITY_CHECK_INTERVAL_MS = 60_000;
/** Timeout for a single connectivity check (3 seconds) */
const CONNECTIVITY_CHECK_TIMEOUT_MS = 3000;
/** URL used for connectivity checks */
const HEARTBEAT_URL = 'https://clients3.google.com/generate_204';

/**
 * Provides network connectivity checks and page visibility monitoring.
 *
 * **Network status**: A background check runs every minute to keep `isOnline` up to date.
 * Use `periodicConnectivityChecks(intervalInMs)` for custom intervals (e.g. STT session).
 *
 * **Page visibility**: uses passive browser events (`visibilitychange`).
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService implements OnDestroy {
  private readonly isOnlineSignal = signal<boolean>(navigator.onLine);
  private readonly isPageVisibleSignal = signal<boolean>(
    typeof document !== 'undefined'
      ? document.visibilityState === 'visible'
      : true,
  );
  private readonly subscription = new Subscription();

  constructor() {
    this.initPageVisibilityMonitoring();
    this.initBackgroundConnectivityChecks();
  }

  /**
   * Starts background connectivity checks every minute to keep `isOnline` up to date.
   * Subscription is cleaned up in ngOnDestroy.
   * @private
   */
  private initBackgroundConnectivityChecks(): void {
    this.subscription.add(
      this.periodicConnectivityChecks(
        BACKGROUND_CONNECTIVITY_CHECK_INTERVAL_MS,
      ).subscribe(),
    );
  }

  /** True when the last connectivity check succeeded. Updated only by `checkConnectivity()`. */
  readonly isOnline = this.isOnlineSignal.asReadonly();

  /** True when the tab is visible (not hidden or minimized). */
  readonly isPageVisible = this.isPageVisibleSignal.asReadonly();

  /**
   * Initializes page visibility monitoring using the Page Visibility API.
   *
   * **Technique**: Uses RxJS `fromEvent` to listen to the `visibilitychange` event
   * on the document. When the event fires, checks `document.visibilityState`
   * to determine if the page is currently visible.
   *
   * **Use case**: Helps distinguish between actual network issues and cases where
   * the user has switched tabs or minimized the browser (where network requests
   * may be throttled or paused by the browser).
   *
   * @private
   */
  private initPageVisibilityMonitoring() {
    if (typeof document === 'undefined') return;

    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => document.visibilityState === 'visible'),
    );

    this.subscription.add(
      visibility$.subscribe((visible) => this.isPageVisibleSignal.set(visible)),
    );
  }

  /**
   * Returns an Observable that runs `checkConnectivity()` periodically at the given interval.
   * When subscribed to, the first check runs immediately, then every `intervalInMs` milliseconds.
   * Each emission is the result of the check; the `isOnline` signal is updated inside the check.
   *
   * @param intervalInMs - Interval between connectivity checks in milliseconds
   * @returns Observable that emits `true` or `false` after each check (signal is already updated)
   */
  periodicConnectivityChecks(intervalInMs: number): Observable<boolean> {
    return timer(0, intervalInMs).pipe(
      switchMap(() => from(this.checkConnectivity())),
    );
  }

  /**
   * Performs a network connectivity check by making an HTTP request.
   *
   * Always performs the HTTP request (does not use `navigator.onLine`), since
   * browser online/offline state is unreliable.
   *
   * **Technique**: Uses the Fetch API with a lightweight HEAD request to a reliable
   * endpoint (Google's generate_204 service) to verify actual internet reachability.
   *
   * **Configuration**:
   * - `method: 'HEAD'`: Lightweight request (no response body)
   * - `mode: 'no-cors'`: Avoids CORS issues, sufficient for connectivity check
   * - `cache: 'no-store'`: Ensures fresh request, not served from cache
   * - Request is aborted after `timeoutMs` (default 3s, aligned with STT/Deepgram disconnect).
   *
   * **Automatic status update**: Updates the `isOnline` signal based on the result.
   *
   * @param timeoutMs - Max time in ms before the request is aborted (default 3000)
   * @returns Promise resolving to `true` if internet is reachable, `false` otherwise
   */
  async checkConnectivity(
    timeoutMs = CONNECTIVITY_CHECK_TIMEOUT_MS,
  ): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return fetch(HEARTBEAT_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(() => {
        clearTimeout(timeoutId);
        this.isOnlineSignal.set(true);
        return true;
      })
      .catch(() => {
        clearTimeout(timeoutId);
        this.isOnlineSignal.set(false);
        return false;
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
